import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import Seo from '../../components/Seo';
import { Button } from '../../components/ui/button';
import { FormCard } from './LegalFormsDatabase';
import { ArrowLeft, ShieldAlert, Stethoscope, ScrollText } from 'lucide-react';

const LegalFormState = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/legal-forms/states/${slug}`);
      setData(r.data);
    } catch (e) { setNotFound(true); } finally { setLoading(false); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-screen pt-28 text-center text-gray-500">Loading…</div>;
  if (notFound || !data) return (
    <div className="min-h-screen pt-28 text-center px-4">
      <Seo exactTitle="Advance Directives & Legal Forms | I Need A Social Worker" noindex path={`/advance-directives/${slug}`} />
      <p className="text-gray-600 font-medium">State not found.</p>
      <Link to="/advance-directives"><Button className="mt-4 bg-secondary hover:bg-secondary-hover text-white">Back to Database</Button></Link>
    </div>
  );

  const sections = data.sections || {};
  const order = ['Advance Directives', 'Medical Orders for Serious Illness', 'Financial & Estate-Planning Forms', 'Minor Child & Caregiver Forms'];
  const notices = data.section_notices || {};
  const stateName = data.state.name;

  // Keyword-rich, distinct SEO title + description so each state page is a strong
  // long-tail landing page (not a thin doorway relying on the central hub).
  const seoTitle = `${stateName} Advance Directives & Legal Forms | DNR, Medical POA, Living Will`;
  const seoDesc = `Official ${stateName} advance directive and end-of-life legal forms — living will (directive to physicians), medical power of attorney / healthcare proxy, out-of-hospital DNR, POLST/MOLST portable medical orders, financial power of attorney and estate-planning forms. Verified, state-specific forms and instructions for social workers, patients and families.`;
  const canonicalPath = `/advance-directives/${slug}`;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://ineedasocialworker.com';
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${stateName} Advance Directives & Legal Forms`,
    description: seoDesc,
    url: `${origin}${canonicalPath}`,
    about: [
      'Advance Directive', 'Living Will', 'Medical Power of Attorney', 'Healthcare Proxy',
      'Do Not Resuscitate (DNR)', 'POLST', 'MOLST', 'Financial Power of Attorney',
    ].map((t) => ({ '@type': 'Thing', name: `${stateName} ${t}` })),
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Advance Directives & Legal Forms', item: `${origin}/advance-directives` },
        { '@type': 'ListItem', position: 2, name: stateName, item: `${origin}${canonicalPath}` },
      ],
    },
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <Seo exactTitle={seoTitle} description={seoDesc} path={canonicalPath} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <section className="bg-primary pt-24 pb-8 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/advance-directives" className="text-secondary text-sm hover:underline flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> All states</Link>
          <div className="flex items-center gap-3 mt-3">
            <span className="w-12 h-12 rounded-full bg-white text-primary font-bold flex items-center justify-center">{data.state.abbr}</span>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold">{stateName} Advance Directives &amp; Legal Forms</h1>
          </div>
          <p className="text-white/80 mt-2">{data.count} official {data.count === 1 ? 'form' : 'forms'} available</p>
          <p className="text-white/70 text-sm mt-2 max-w-3xl">
            Find official {stateName} advance directive forms — living will (directive to physicians), medical power of attorney / healthcare proxy,
            out-of-hospital DNR, POLST/MOLST portable medical orders, and financial &amp; estate-planning documents — with state-specific instructions.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        {data.count === 0 && (
          <div className="bg-white rounded-xl border p-10 text-center">
            <ScrollText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-600 font-medium">Not currently available as a separate official form in this state.</p>
            <p className="text-gray-400 text-sm mt-1">We only publish official, verified forms. Official {data.state.name} forms are being added.</p>
          </div>
        )}
        {order.filter((sec) => sections[sec]?.length).map((sec) => (
          <div key={sec}>
            <div className="flex items-center gap-2 mb-1">
              {sec.startsWith('Medical') ? <Stethoscope className="w-5 h-5 text-secondary" /> : <ScrollText className="w-5 h-5 text-secondary" />}
              <h2 className="font-serif text-2xl font-bold text-primary">{sec}</h2>
            </div>
            {sec.startsWith('Medical') && (
              <div className="bg-secondary/10 border border-secondary/30 rounded-lg p-3 text-sm text-primary mb-4" data-testid="medical-order-notice">{data.medical_order_notice}</div>
            )}
            {!sec.startsWith('Medical') && notices[sec] && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-sm text-primary mb-4" data-testid={`section-notice-${sec.replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`}>{notices[sec]}</div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              {sections[sec].map((f) => <FormCard key={f.form_id} f={f} />)}
            </div>
          </div>
        ))}

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-sm text-amber-800" data-testid="legal-disclaimer">
          <ShieldAlert className="w-5 h-5 flex-shrink-0 text-amber-600" />
          <p>{data.disclaimer}</p>
        </div>
      </div>
    </div>
  );
};

export default LegalFormState;
